import {msg} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'

import {OPTIMIZE_LEVELS, type OptimizeLevel} from '#/lib/document/optimize'
import {formatFileSize} from '#/lib/document/util'
import {atoms as a, useTheme} from '#/alf'
import {Button} from '#/components/Button'
import * as Menu from '#/components/Menu'
import {Text} from '#/components/Typography'

/**
 * What the compressor did to the file behind this carousel. `originalSize` is
 * the file the user picked, which is what makes an alternative level possible.
 */
export type PdfCarouselCompression = {
  level: OptimizeLevel
  status: 'running' | 'done'
  originalSize: number
  optimizedSize: number
  /** False when the optimizer kept the original bytes */
  applied: boolean
}

/**
 * Composer-only control for the compressor, standing in for the size readout:
 * the number is what the post will upload, and the saving beside it is the
 * point of the feature. Changing the level re-compresses the picked file and
 * uploads the result, so the menu doubles as the explanation.
 */
export function CompressionChip({
  compression,
  onChangeLevel,
}: {
  compression: PdfCarouselCompression
  onChangeLevel: (level: OptimizeLevel) => void
}) {
  const {_} = useLingui()
  const t = useTheme()
  const savedPercent =
    compression.applied && compression.originalSize > 0
      ? Math.round(
          (1 - compression.optimizedSize / compression.originalSize) * 100,
        )
      : 0
  const labels: Record<OptimizeLevel, {label: string; hint: string}> = {
    lossless: {
      label: _(msg`Original quality`),
      hint: _(msg`Repack only — images are untouched`),
    },
    high: {label: _(msg`High`), hint: _(msg`Sharpest — about 200 DPI`)},
    balanced: {label: _(msg`Balanced`), hint: _(msg`About 150 DPI`)},
    small: {label: _(msg`Small`), hint: _(msg`About 100 DPI`)},
  }

  return (
    <Menu.Root>
      <Menu.Trigger label={_(msg`PDF compression`)}>
        {({props}) => (
          <Button
            {...props}
            label={_(msg`PDF compression`)}
            variant="ghost"
            color="secondary"
            size="tiny"
            // Not `round`: at these sizes a round button is a fixed 25x25 icon
            // target, which would clip the reading.
            shape="rectangular"
            // The saving is the point of the control, so it must not be the
            // thing that gets squeezed when the title is long.
            style={{flexShrink: 0}}>
            <Text
              numberOfLines={1}
              style={[a.text_xs, t.atoms.text_contrast_medium]}>
              {savedPercent > 0
                ? `${formatFileSize(compression.optimizedSize)} · −${savedPercent}%`
                : formatFileSize(compression.optimizedSize)}
            </Text>
          </Button>
        )}
      </Menu.Trigger>
      <Menu.Outer>
        <Menu.Group>
          {OPTIMIZE_LEVELS.map(level => (
            <Menu.Item
              key={level}
              label={`${labels[level].label} — ${labels[level].hint}`}
              onPress={() => onChangeLevel(level)}>
              <Menu.ItemText>{labels[level].label}</Menu.ItemText>
              <Menu.ItemRadio selected={compression.level === level} />
            </Menu.Item>
          ))}
        </Menu.Group>
      </Menu.Outer>
    </Menu.Root>
  )
}
